import React from 'react';
import _ from 'lodash';
import { EnabledDynamicCorpParams } from '@scale/shared/corp/dynamicCorpToolManager';
import { IS_FEDERAL } from '@scale/frontend-lib/constants';
import { useAllDynamicCorpPageTitles } from 'react-scale/corp/hooks/useAllDynamicCorpPageTitles';
import { BrowserPaths } from '@scale/shared/corpSearch';

export interface CorpNavLink {
  title: string;
  href: string;
  directoryLink?: string;
  isExternal?: boolean;
  isHidden?: boolean;
  inline?: boolean;
  description?: string;
}

export interface CorpNavSection {
  title: string;
  links: CorpNavLink[];
}

const DYNAMIC_CORP_TOOLS_SECTION = {
  title: 'Dynamic Corp Tools Section',
  links: _.map(EnabledDynamicCorpParams, (value: $FIXME, route: $FIXME) => {
    return {
      title: value?.pageTitle ?? '',
      href: `/corp/dct/${route}`,
      isHidden: !!value?.hidden,
    };
  }),
};

/**
 * REMINDER: Whenever a new Corp Tool is created, remember to add its details to the `HomePageCorpToolsData` in
 * `scaleapi/frontend/corp/views/HomePageCorpToolsData.ts` or it won't be displayed on the relevant pages.
 * */

const corpNavSections: CorpNavSection[] = [
  {
    title: 'At a glance',
    links: [
      {
        title: 'Corp Directory',
        isExternal: true,
        href: 'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.qyq8uidzmoue',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.qyq8uidzmoue',
      },
    ],
  },
  {
    title: 'Demos',
    links: [
      {
        title: 'All',
        href: '/corp/demo',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.19zb10vca27b',
        isExternal: true,
      },
      {
        title: 'NLP',
        href: '/corp/nlp_demo',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.75h8h0107yhf',
        isExternal: true,
      },
      {
        title: 'GIS Global Map Viewer',
        href: '/corp/global_map_viewer',
        // FIXME - directoryLink update
      },
    ],
  },
  {
    title: 'Field Engineering',
    links: [
      {
        title: 'Task Actions',
        href: '/corp/task_actions',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.ddj8367jw3n1',
      },
      {
        title: 'User Actions',
        href: '/corp/user_actions',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.tpz3y8c5yo3',
      },
      {
        title: 'Batch Actions',
        href: '/corp/batch_actions',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.ith6a7cyz8eb',
      },
      {
        title: 'Generate Uploader Link',
        href: 'https://scale-sales-uploader.now.sh/generate-link',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.4r5110bdpjar',
        isExternal: true,
        isHidden: IS_FEDERAL,
      },
      {
        title: 'Upload Files to Scale Storage',
        href: '/corp/v2/upload_files_to_s3',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.jda1qzk8zp0h',
      },
      {
        title: 'Create Cruise Control Task',
        href: '/corp/create_cruise_control_task',
      },
    ],
  },
  {
    title: 'Eng Tools',
    links: [
      {
        title: 'Snowflake Status',
        href: '/corp/snowflake_status',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.kbth5dpfjvlh',
      },
      {
        title: 'Corp API Keys',
        href: '/corp/dct/corp-api-keys',
      },
      {
        title: 'Transform S3 URL',
        href: '/corp/eng/transform_s3_url',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.66w88v1rqf3y',
      },
      {
        title: 'Fetch S3 file via Proxy',
        href: '/corp/proxy_fetch',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.atfgawfpsjsk',
        isHidden: IS_FEDERAL,
      },
      {
        title: 'View Past Script Runs',
        href: '/corp/scripts',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.3yzq2lnesg4n',
      },
      {
        title: 'Random Configs',
        href: '/corp/eng/random_configs',
      },
      {
        title: 'Launch Endpoints',
        href: '/corp/launch/endpoints',
      },
    ],
  },
  {
    title: 'Delivery Systems',
    links: [
      {
        title: 'Project SLA Groups',
        href: '/corp/project_groups',
        directoryLink: 'https://scale.atlassian.net/wiki/spaces/OP/pages/72941741/Project+Groups',
      },
      {
        title: 'Project Roles Authorization',
        href: '/corp/project_roles_auth',
      },
      {
        title: 'Staffing Exception Requests',
        href: '/corp/staffing_exception_requests',
      },
      {
        title: 'Staffing Authorizations',
        href: '/corp/staffing_authorizations',
      },
      {
        title: 'Bulk Action Batches',
        href: '/corp/bulk_action_batches',
      },
      {
        title: 'Bulk Project Configs',
        href: '/corp/bulk_project_configs',
      },
      {
        title: 'QAR (Queue Audit + Repair)',
        href: '/corp/QAR',
        directoryLink: 'https://scaleapi.slack.com/archives/C028EKF4337',
      },
    ],
  },
  {
    title: 'General Ops',
    links: [
      {
        title: 'Mass Spoof',
        href: '/corp/dct/mass-spoof',
        description: 'Analyze queue status, assignments, missions, and webinars for multiple users',
      },
      {
        title: 'AI Review - Full Story',
        href: '/corp/dct/ai-review-full-story',
        description: 'Generate full story analysis for AI review contributors',
      },
      {
        title: 'Delivery Portal',
        href: '/corp/qm',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.gqp2i030jj8',
      },
      {
        title: 'Task Type Configs',
        href: '/corp/task_type_manager',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.hx5bgoaaa3zw',
      },
      {
        title: 'Create Project',
        href: '/corp/scalers/new_project',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.dfaedximqley',
      },
      {
        title: 'Needs Corp',
        href: '/corp/bpo/needs_corp',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.lpyrxq8vjwyq',
      },
      {
        title: 'Update Instructions',
        href: '/corp/update_instructions',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.c900t92wk1rt',
      },
      {
        title: 'Instruction Taxonomies',
        href: '/corp/instruction_taxonomies',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.6f2f0swq9tx',
      },
      {
        title: 'Recall Tasks',
        href: '/corp/bpo/recall_tasks',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.pgz4a8xbhot4',
      },
      {
        title: 'Flamingo Actions',
        href: '/corp/projects/flamingo_actions',
        directoryLink: 'https://scale-api.quip.com/kDEqAZUX9Gog/Corp-Directory#GTPACAgt1zX',
      },
      {
        title: 'Demote Taskers',
        href: '/corp/bpo/demote_scalers',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.a3zoemmdcbjc',
      },
      {
        title: 'SLAs',
        href: '/corp/sla',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.5rfhtsopfsap',
      },
      {
        title: 'Add SLA',
        href: '/corp/sla/new',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.hy59le7n1we',
      },
      {
        title: 'Rerun Task Billing',
        href: '/corp/dct/rerun-task-billing',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.achno3xks80v',
      },
      {
        title: 'Training Courses',
        href: '/corp/courses',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.d092mhar5vcv',
      },
      {
        title: 'Remedial Courses',
        href: '/corp/remedial_courses',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.6jixzicx34pq',
      },
      {
        title: 'Onboarding Center Management',
        href: '/corp/permissions',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.4a1jjddh9uaw',
      },
      {
        title: 'Scenarios',
        href: '/corp/scenarios',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.szesydea4fcz',
      },
      {
        title: 'Corp Explain',
        href: '/corp/explain',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.e8sh3axreue0',
      },
      {
        title: 'Customer App Tools (Campaigns, Failure Modes)',
        href: 'https://dashboard.scale.com/corp/customer_app', // use absolute URL so that the Next route is matched, not the react-router logic
        directoryLink: 'https://scale.enterprise.slack.com/archives/C07DNEQPSG1',
      },
      {
        title: 'Bulba Bug Model Editor',
        href: '/corp/bulba_bug_editor',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#bookmark=id.c5z5x7eavt3t',
      },
      {
        title: 'Email to Tasker ID Conversion or Subtask/Task ID Conversion',
        href: '/corp/ops_utils',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.ftywz2krgql4',
      },
      {
        title: 'Course Utils',
        href: '/corp/course_utils',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.4h60zapqimwx',
      },
      {
        title: 'Assign Benchmark',
        href: '/corp/assign_benchmark',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.tigcfwoh8bir',
      },
      {
        title: 'Unexclude Workers',
        href: '/corp/unexclude_workers',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.u1mrrh9yb5wh',
      },
      {
        title: 'Increase Queue Priority',
        href: '/corp/increase_priority',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.fpdmn9ovzfui',
      },
      {
        title: 'GIS Task Pipeline Tool',
        href: '/corp/gis_task_pipeline_tool',
        directoryLink:
          'https://docs.google.com/document/d/1rtTU4c-9PWwXTu3MeM2L93WYYWljkFrhBDNjUzAVt-s/edit#',
      },
      {
        title: 'GIS Changesets Quick Editor',
        href: '/corp/gis_json_quick_editor',
        directoryLink:
          'https://scale.atlassian.net/wiki/spaces/EPD/pages/401309863/How+to+use+GMV+Quick+Editor',
      },
      {
        title: 'Pipeline Special Actions',
        href: '/corp/pipeline_special_actions',
        directoryLink:
          'https://docs.google.com/document/d/1sE2mz5PDLhYcdMDUz7wUDANshmfw3Ed1BzmcrTggJmA/edit#',
      },
      {
        title: 'Bee TTS Utils',
        href: '/corp/bee_tts_utils',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.idavpj36eshs',
      },
      {
        title: 'Bee RLHF Utils',
        href: '/corp/bee_rlhf_utils',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.l2jy1ufpejw9',
      },
      {
        title: 'Bulba Generic Project',
        href: '/corp/ops/bulba_generic_project',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.mih8zpkmw7yl',
      },
      {
        title: 'Sensitive Content Utils',
        href: '/corp/sensitive_content_utils',
        directoryLink:
          'https://docs.google.com/document/d/1prm8ahORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.idavpj36eshs',
      },
      {
        title: 'Activity Tracker',
        href: '/corp/activity_tracker',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.x6yhvsd42qee',
      },
      {
        title: 'Scale Labs Experiments',
        href: '/corp/scale_labs',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/',
      },
      {
        title: 'Catalog Taxonomy Tool',
        href: '/corp/catalog_taxonomy_tool',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.uzjtnqo67105',
      },
    ],
  },
  {
    title: 'Long Tasks',
    links: [
      {
        title: 'Lidar Tasks',
        href: '/corp/lidartasks',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.pk60cfke85ab',
      },
      {
        title: '2D Tasks',
        href: '/corp/2d_tasks',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.3w42lei5evl7',
      },
    ],
  },
  {
    title: 'Supply',
    links: [
      {
        title: 'Supply Lever Dash',
        href: '/corp/supply_lever_dash',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.7fff7eukukl',
      },
      {
        title: 'Apply Pipeline V3 Traits',
        href: '/corp/v3traits',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.axnqq4tfgo4x',
      },
    ],
  },
  {
    title: 'Quality',
    links: [
      {
        title: 'Quality Lab',
        href: '/corp/quality_lab',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.jnch3bcrv4k0',
      },
      {
        title: 'QA Hub',
        href: '/corp/qa_hub',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.p2ws0kce7x2g',
      },
    ],
  },
  {
    title: 'Experiments Platform',
    links: [
      {
        title: 'Create Experiment',
        href: '/corp/genai-ops-hub/experiment-platform/create-experiment',
      },
      {
        title: 'View Experiments',
        href: '/corp/genai-ops-hub/experiment-platform',
      },
    ],
  },
  {
    title: 'Worker Source',
    links: [
      {
        title: 'Create Worker Source',
        href: '/corp/worker-source/create_source',
      },
      {
        title: 'View Worker Sources',
        href: '/corp/worker-source/view_sources',
      },
    ],
  },
  {
    title: 'Payouts',
    links: [
      { title: 'Mission Config', href: '/corp' + BrowserPaths.MissionConfig },
      {
        title: 'Recalculate Work And Payouts',
        href: '/corp/dct/recalculate-work-and-payouts',
      },
      {
        title: 'Work Submission Pay Recalculation',
        href: '/corp/dct/work-submission-pay-recalculation',
      },
      {
        title: 'Update Payment Status',
        href: '/corp/dct/update-payment-status',
      },
      {
        title: 'Add Reward',
        href: '/corp/rewards',
      },
      {
        title: 'Create or Update Payment Amounts',
        href: '/corp/dct/create-or-update-payment-amounts',
      },
      {
        title: 'Backtest Pay Configs',
        href: '/corp/backtest_pay',
      },
      {
        title: 'Create or Update Minimum Wage Rates',
        href: '/corp/minimum-wages',
      },
      {
        title: 'Manage Pay Strata Permissions',
        href: '/corp/pay-strata-permissions',
      },
      {
        title: 'Create or Update Pay Traits',
        href: '/corp/pay-traits',
      },
      // note: /project-pay-traits page was deprecated in favor of /pay-traits, but still keeping here
      //  to inform and redirect to the new page.
      {
        title: 'Create or Update Project Pay Traits [deprecated]',
        href: '/corp/pay/strata/project-pay-traits',
      },
      {
        title: 'Create or Update Pay Buckets',
        href: '/corp/pay-buckets',
      },
      {
        title: 'Manage Project Strata Pay Configs',
        href: '/corp/pay-configs',
      },
      {
        title: 'View Worker Pay Strata Information',
        href: '/corp/worker-pay-strata',
      },
      {
        title: 'View Project Strata Information',
        href: '/corp/project-strata',
      },
    ],
  },
  {
    title: 'Billing',
    links: [
      { title: 'MAXREV', href: '/corp' + BrowserPaths.MAXREV },
      {
        title: 'Bulk Billing Structure',
        href: '/corp' + BrowserPaths.ProjectsBillingStructure,
        directoryLink: '',
      },
      {
        title: 'AHT Dampener',
        href: '/corp' + BrowserPaths.AHTDampener,
        directoryLink: '',
      },
      {
        title: 'Revenue Groups',
        href: '/corp' + BrowserPaths.RevenueGroupsDashboard,
        directoryLink: '',
      },
      {
        title: 'Revenue Groups V2',
        href: '/corp' + BrowserPaths.RevenueGroupsDashboardV2,
        directoryLink: '',
      },
    ],
  },
  {
    title: 'Billing Hub',
    links: [{ title: 'Acceptance Billing', href: '/corp' + BrowserPaths.AcceptanceBilling }],
  },
  {
    title: 'Tasker Management',
    links: [
      {
        title: 'Worker Slack Channels',
        href: '/corp/scalers/slack_channels',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.ie7zsfk49qwk',
      },
      {
        title: 'Worker Teams',
        href: '/corp/teams',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.ie7zsfk49qwk',
      },
      {
        title: 'Qualifications',
        href: '/corp/scaler/qualifications',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.3oa9ve32zt5p',
      },
      {
        title: 'BPO Permissions',
        href: '/corp/bpo/mass_permissioning',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.k742tgr2k4cb',
      },
      {
        title: 'Create Tasker',
        href: '/corp/scalers/new',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.3ebyibqk74ya',
      },
      {
        title: 'Signup Flows',
        href: '/corp/signup-flow',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.njmpjdfnt8hs',
      },
      {
        title: 'Undisable Taskers',
        href: '/corp/undisable',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.x50yyg578p9',
      },
      {
        title: 'Announcements',
        href: '/corp/scalers/announcements',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.dxgden7xmqbz',
      },
      {
        title: 'Add Reward',
        href: '/corp/rewards',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.1v8zm077r1lv',
      },
      {
        title: 'Enforcement Center',
        href: '/corp/enforcement',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.x50yyg578p9',
      },
    ],
  },
  {
    title: 'Analysis',
    links: [
      {
        title: 'Speed Audit',
        href: '/corp/speed_audit',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.l7ug1tpmxnf3',
      },
      {
        title: 'SME Audit',
        href: '/corp/sme_audit',
      },
    ],
  },
  {
    title: 'Linters',
    links: [
      {
        title: 'Linter Docs',
        href: '/corp/linter_config_docs',
      },
      {
        title: 'Response Linter Project Info',
        href: '/corp/2d_linting',
      },
      {
        title: 'Tune Response Linters',
        href: '/corp/tune_2d_linting',
      },
      {
        title: 'Lidar Linter Project Info',
        href: '/corp/lidarlinting',
      },
      {
        title: 'Copy/Remove linters from multiple projects',
        href: '/corp/copy-linters-from-multiple-projects',
      },
    ],
  },
  {
    title: 'Growth',
    links: [
      {
        title: 'Resume Screening Course Configs',
        href: '/corp/resume_screening_course_configs',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.xqlxbvpw4ryw',
      },
    ],
  },
  {
    title: 'Data Collection',
    links: [
      {
        title: 'Consent Forms',
        href: '/corp/consent_forms',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.t02lxvla5svb',
      },
      {
        title: 'Demographic Fields',
        href: '/corp/demographic_information_service',
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.gd1rhfk6eoz6',
      },
    ],
  },
  {
    title: 'Sensor Fusion Unification',
    links: [
      {
        title: 'Redo Recall Wizard',
        href: '/corp/sfu_redo_recall',
      },
      {
        title: 'SFU Task Tracker',
        href: '/corp/sfu_task_tracker',
      },
    ],
  },
  {
    title: 'Nucleus',
    links: [
      {
        title: 'Nucleus Project Syncs',
        href: '/corp/nucleus_project_sync',
      },
      {
        title: 'Nucleus API Keys',
        href: '/corp/dct/nucleus-api-keys',
      },
    ],
  },
  {
    title: 'Enterprise Ops',
    links: [
      {
        title: 'Enterprise Hub',
        href: '/corp' + BrowserPaths.EnterpriseHub,
        directoryLink:
          'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit?tab=t.0#heading=h.pzd7wppdy136',
      },
    ],
  },
  DYNAMIC_CORP_TOOLS_SECTION,
];

export const pinnedCorpNavLinks: CorpNavLink[] = [
  {
    title: 'GenAI Ops Hub',
    href: '/corp/genai-ops-hub/projects',
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.l7ug1tpmxnf3',
    description: 'Navigate to GenAI Ops Hub',
  },
  {
    title: 'Speed Audit',
    href: '/corp/v2/speed_audit',
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.l7ug1tpmxnf3',
    description:
      'Want to look at lots of subtasks/task attempts really quick and not open a bunch of corp lookup pages?',
  },
  {
    title: 'Training Courses',
    href: '/corp/courses',
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.d092mhar5vcv',
    description:
      'List of training courses that are currently in the system. You can activate or deactivate the existing courses. You can also build a new course by scrolling to the bottom of the page.',
  },
  {
    title: 'Expert Qualifications',
    href: '/corp/scalers/qualifications',
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.d092mhar5vcv',
    description: 'Expert tasker qualifications and qualification lists for worker teams',
  },
  {
    title: 'Tasker Account Testing Center',
    href: '/corp/scalers/test',
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.d092mhar5vcv',
    description: 'Seemlessly Test Tasker, Worker, and Expert platform accounts',
  },
  {
    title: 'BPO Permissions',
    href: '/corp/bpo/mass_permissioning',
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.k742tgr2k4cb',
    description:
      'Clear assigned project, Mass assign project, Mass add permissions, Mass add worker sources, Mass create accounts',
  },
  {
    title: 'Recall Tasks',
    href: '/corp/bpo/recall_tasks',
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.pgz4a8xbhot4',
    description:
      'Can use to recall a list of completed subtasks into review mode or subtask state.',
  },
  {
    title: 'Corp Explain',
    href: '/corp/explain',
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.e8sh3axreue0',
    description: 'Troubleshoot tasker queue',
  },
  {
    title: 'Upload Files to Scale Storage',
    href: '/corp/v2/upload_files_to_s3',
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.jda1qzk8zp0h',
    description: 'Uploads local files to our public S3 bucket scale_static_assets.',
  },
  {
    title: 'Scenarios',
    href: '/corp/scenarios',
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.szesydea4fcz',
    description:
      'List of scenarios that exist in the system and the links to the actual scenario on remotasks.',
  },
  {
    title: 'Delivery Portal',
    href: '/corp/qm',
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.elprj4k2dl4q',
    description: 'The hub for delivery operations.',
  },
  {
    title: 'Lidar Tasks',
    href: '/corp/lidartasks',
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.pk60cfke85ab',
    description:
      'List of LiDAR tasks that contain information on individual LiDAR tasks such as last reviewer, assigned reviewer, task link, number of dynamic cuboids, number of stationary cuboids etc.',
  },
  {
    title: 'Revenue Groups Dashboard',
    href: '/corp' + BrowserPaths.RevenueGroupsDashboard,
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.7pc9obuyoqf5',
    description: 'Dashboard to create and manage revenue groups.',
  },
  {
    title: 'Revenue Groups V2',
    href: '/corp' + BrowserPaths.RevenueGroupsDashboardV2,
    directoryLink:
      'https://docs.google.com/document/d/1prm8hORruaV-xCdTu1Xrq6IzM64znEk7Rdrw7jMpOvM/edit#heading=h.7pc9obuyoqf5',
    description: 'Dashboard to create and manage revenue groups.',
  },
];

export const useCorpNavSections = (): CorpNavSection[] => {
  const allPageTitles = useAllDynamicCorpPageTitles();
  const dynamicCorpToolsSection = React.useMemo(
    () => ({
      title: 'Dynamic Corp Tools (DCT)',
      links: allPageTitles.map(({ name, pageTitle }: { name: string; pageTitle: string }) => ({
        title: pageTitle,
        href: `/corp/dct/${name}`,
      })),
    }),
    [allPageTitles],
  );
  return React.useMemo(
    () => [
      ...corpNavSections.filter(section => section !== DYNAMIC_CORP_TOOLS_SECTION),
      dynamicCorpToolsSection,
    ],
    [dynamicCorpToolsSection],
  );
};
